import React, { FC, useMemo, useState } from 'react';
import { VictoryAxis, VictoryChart, VictoryLine } from 'victory';
import { ButtonToggles } from '@eika/radio-button';
import { GraphDataPeriod } from '../../../../../../domain/Fund';
import { useFundGraphData } from './useFundGraphData';
import { Spinner } from '../../../../../../common/share/ShareComponents';
import DropdownMenu from '../../../../../../common/DropdownMenu/DropdownMenu';
import { formatAmount } from '../../../../../../common/util/Functions';
import './SavingProfileGraph.scss';
import { getGraphTheme, zeroAxisStyles } from './savingProfileGraphTheme';
import { getTicks, GraphDataPeriodMap } from './graphUtils';

type SavingProfileGraphProps = {
    isin: string;
};
const SavingProfileGraph: FC<SavingProfileGraphProps> = ({ isin }) => {
    const selectPeriodOptions = useMemo(
        () =>
            Object.entries(GraphDataPeriodMap)
                .filter(([_, value]) => !!value)
                .map(([period, data]) => ({ id: period, text: data?.fullLabel })),
        []
    );

    const radioPeriodOptions = useMemo(
        () =>
            Object.entries(GraphDataPeriodMap)
                .filter(([_, value]) => !!value)
                .map(([period, data]) => ({ value: period, label: data?.shortLabel })),
        []
    );

    const [period, setPeriod] = useState(GraphDataPeriod.LAST_5_YEARS);

    const { graphData, pending, previouslyLoaded, error } = useFundGraphData({ isin, period });

    const currentData = useMemo(() => graphData?.series[0].points, [graphData]);

    const edgeDates = useMemo(
        () => currentData?.filter((_, i, array) => [0, array.length - 1].includes(i))?.map((point) => point.x),
        [currentData]
    );

    const zeroAxisTicks = edgeDates?.map((date) => ({ x: date, y: 10_000 }), [edgeDates]);

    const edgeValues = useMemo(
        () =>
            currentData?.reduce(
                (edges, point) => ({
                    min: point.y < edges.min ? point.y : edges.min,
                    max: point.y > edges.max ? point.y : edges.max,
                }),
                { min: 10000, max: 10000 }
            ),
        [currentData]
    );

    const ticks = useMemo(() => getTicks(edgeValues), [edgeValues]);

    return (
        <div className="saving-profile-graph">
            <h3>Historisk verdiutvikling</h3>
            {!error && (
                <div className="-content">
                    <div className="graph-wrapper -single">
                        {pending && <Spinner />}
                        {currentData && (
                            <VictoryChart theme={getGraphTheme()} domainPadding={{ x: 2, y: 2 }}>
                                <VictoryAxis
                                    dependentAxis
                                    tickValues={ticks}
                                    tickFormat={(tick) => `${formatAmount((tick - 10_000) / 100, true)} %`}
                                />
                                <VictoryAxis
                                    crossAxis
                                    tickValues={edgeDates}
                                    tickFormat={GraphDataPeriodMap[period]?.dateParse}
                                />
                                <VictoryLine
                                    data={currentData}
                                    animate={previouslyLoaded ? false: { animationWhitelist: ['onLoad'] }}
                                />
                                <VictoryLine data={zeroAxisTicks} style={zeroAxisStyles} />
                            </VictoryChart>
                        )}
                    </div>
                    <div className="only-for-desktop">
                        <DropdownMenu
                            selected={period}
                            optionList={selectPeriodOptions}
                            click={(_, __, d) => setPeriod(d.id)}
                        />
                    </div>
                    <div className="only-for-mobile">
                        <ButtonToggles
                            label="Velg periode å vise i grafen"
                            onChange={(period) => setPeriod(period as GraphDataPeriod)}
                            selectedOption={period}
                            options={radioPeriodOptions}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SavingProfileGraph;
